import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon/Icon.js';
import styles from './Input.module.scss.js';
import useHandleInputCursor from './useHandleInputCursor.js';
import { formatSerialNumber } from './InputUtils.js';

/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/jsx-fragments */
function Input(_a) {
    var placeholder = _a.placeholder, onChange = _a.onChange, hasError = _a.hasError, value = _a.value, icon = _a.icon, disabled = _a.disabled, type = _a.type, onClick = _a.onClick, className = _a.className, dataTestId = _a.dataTestId, errorMessage = _a.errorMessage, focus = _a.focus, minLength = _a.minLength, minLengthErrorText = _a.minLengthErrorText, maxLength = _a.maxLength, onIconClick = _a.onIconClick, disableLabelAnimation = _a.disableLabelAnimation, autoComplete = _a.autoComplete, mandatory = _a.mandatory, mandatoryInfoText = _a.mandatoryInfoText, disableAutofill = _a.disableAutofill, name = _a.name, inputMode = _a.inputMode, scrollIntoVIew = _a.scrollIntoVIew, focusAndScrollIntoView = _a.focusAndScrollIntoView, defaultCursorBehavior = _a.defaultCursorBehavior, isSerialNumber = _a.isSerialNumber;
    var inputEl = useRef(null);
    var _b = useState(''), validationError = _b[0], setValidationError = _b[1];
    var updateCursorPosition = useHandleInputCursor(inputEl, value, defaultCursorBehavior, isSerialNumber).updateCursorPosition;
    useEffect(function () {
        if (focus && inputEl.current) {
            inputEl.current.focus();
        }
    }, [focus, inputEl.current]);
    useEffect(function () {
        var timeoutId;
        if (focusAndScrollIntoView && inputEl.current) {
            inputEl.current.focus();
            timeoutId = setTimeout(function () {
                var inputElement = inputEl.current;
                if (inputElement) {
                    inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 350);
        }
        return function () {
            clearTimeout(timeoutId);
        };
    }, [focusAndScrollIntoView, inputEl.current]);
    useEffect(function () {
        if (scrollIntoVIew && inputEl.current) {
            var inputOffsetTop = inputEl.current.getBoundingClientRect().top;
            var scrollAmount = inputOffsetTop - window.innerHeight / 2;
            window.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        }
    }, [scrollIntoVIew, inputEl.current]);
    var handleInputChange = function (event) {
        var inputValue = event.currentTarget.value;
        var isValid = true;
        setValidationError('');
        if (minLength && inputValue.trim().length < minLength) {
            isValid = false;
            if (minLengthErrorText) {
                setValidationError(minLengthErrorText);
            }
        }
        var currentValue = isSerialNumber ? formatSerialNumber(inputValue) : inputValue;
        updateCursorPosition(currentValue, event);
        onChange(currentValue, isValid, name);
    };
    var handleKeyPress = function (e) {
        var _a;
        if (e.key === 'Enter') {
            (_a = e.target) === null || _a === void 0 ? void 0 : _a.blur();
        }
    };
    var errorMsg = errorMessage || validationError;
    var fieldError = hasError || validationError;
    var placeholderText = mandatory ? "".concat(placeholder, "*") : placeholder;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(styles['input-container'], className) },
            React.createElement("label", { id: "".concat(dataTestId, "-label"), className: classNames(styles.wrapper, disabled && styles.disabled, fieldError && styles.error, icon !== '' && styles['with-icon'], (disableLabelAnimation || value !== '') && styles['has-value']), onClick: onClick },
                React.createElement("form", { autoComplete: autoComplete },
                    React.createElement("input", { id: dataTestId, className: classNames(styles.input, disabled && styles.disabled, disableAutofill && styles.disableAutofill), type: type, value: value, onChange: handleInputChange, minLength: minLength, maxLength: maxLength, ref: inputEl, onKeyDown: handleKeyPress, disabled: disabled, autoComplete: autoComplete, inputMode: inputMode })),
                React.createElement("p", { className: classNames(styles.label, type === 'password' && styles['label-for-password']) }, placeholderText),
                icon && (React.createElement(Icon, { icon: icon, className: classNames(styles['input-field-icon'], disabled && styles.disabled), onClick: onIconClick }))),
            errorMsg && React.createElement("p", { className: styles['error-message'] }, errorMsg),
            mandatory && mandatoryInfoText && !errorMsg && React.createElement("p", { className: styles.assistive }, mandatoryInfoText))));
}
Input.propTypes = {
    placeholder: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    hasError: PropTypes.bool,
    value: PropTypes.string,
    icon: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    errorMessage: PropTypes.string,
    focus: PropTypes.bool,
    minLength: PropTypes.number,
    minLengthErrorText: PropTypes.string,
    maxLength: PropTypes.number,
    onIconClick: PropTypes.func,
    disableLabelAnimation: PropTypes.bool,
    autoComplete: PropTypes.string,
    mandatory: PropTypes.bool,
    mandatoryInfoText: PropTypes.string,
    disableAutofill: PropTypes.bool,
    name: PropTypes.string,
    inputMode: PropTypes.string,
    scrollIntoVIew: PropTypes.bool,
    focusAndScrollIntoView: PropTypes.bool,
};
Input.defaultProps = {
    hasError: false,
    value: '',
    icon: '',
    disabled: false,
    type: 'text',
    onClick: function () { return null; },
    onIconClick: function () { return null; },
    className: '',
    dataTestId: null,
    errorMessage: '',
    focus: false,
    minLength: null,
    minLengthErrorText: null,
    maxLength: null,
    disableLabelAnimation: false,
    autoComplete: '',
    mandatory: false,
    mandatoryInfoText: null,
    disableAutofill: false,
    name: '',
    inputMode: 'text',
    scrollIntoVIew: false,
    focusAndScrollIntoView: false,
    defaultCursorBehavior: false,
    isSerialNumber: false,
};

export { Input as default };
